import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../Api";
import { BusinessForm, BusinessDTO, BeneficialOwner } from "../../types/Business";
import UnsavedChangesGuard from "../../UnsavedChanges"
import { TrashIcon } from "@heroicons/react/24/outline";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as regex from "../../constants/regex";
import * as css from "../../constants/css";
import { ENTITY_TYPE_OPTIONS} from "../../constants/business";
import FormInput from "../../controls/FormInput";
import { FormSelect } from "../../controls/FormSelect";

const businessFormSchema = yup.object().shape({
  businesses: yup.array().of(
    yup.object().shape({
      name: yup.string().required("Business Name is required"),
      entityType: yup.string().required("Entity Type is required"),
      countryOfOrganization: yup.string().required("Country of Organization is required"),
      countryOfTaxResidency: yup.string().required("Country of Tax Residency is required"),
      dateOfFormation: yup.string().required("Date of Formation is required").test('not-future', 'Date of formation cannot be in the future', function(value) {
        if (!value) return true; // Let required validation handle empty values
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today
        return selectedDate <= today;
      }),  
      
      // Additional DWP fields
      isPhysicalSameAsMailing: yup.boolean(),
      canProvideProofOfAddress: yup.boolean(),
      proofOfAddressExplanation: yup.string().when('canProvideProofOfAddress', {
        is: false,
        then: (schema) => schema.required("Please explain why you cannot provide proof of address"),
        otherwise: (schema) => schema.optional()
      }),

      // Business Contact Information
      businessEmail: yup.string().required("Business Email is required").matches(regex.email, "Must be a valid email"),
      businessPhone: yup.string().required("Business Phone is required").matches(regex.phoneNumber, "Phone number must be 4-12 digits"),
      jurisdictionOfFormation: yup.string().required("Jurisdiction of Formation is required"),
      businessTaxId: yup.string().required("Business Tax ID is required"),

      // Primary Contact Information
      primaryContact: yup.string().required("Name is required"),
      primaryContactEmail: yup.string().required("Email is required").matches(regex.email, "Must be a valid email"),
      primaryContactPhone: yup.string().required("Phone is required").matches(regex.phoneNumber, "Phone number must be 4-12 digits"),
      
      // Principal Place of Business (Legal Address)
      legalAddressLine1: yup.string().required("Street Address is required"),
      legalAddressCity: yup.string().required("City is required"),
      legalAddressState: yup.string().required("State/Region is required"),
      legalAddressZipCode: yup
        .string()
        .required("Postal/Zip Code is required")
        .matches(regex.zipCode, "Enter a valid ZIP/postal code"),
      legalAddressCountry: yup.string().required("Country is required"),
      
                    // Mailing Address - only required when isPhysicalSameAsMailing is false
       mailingAddressLine1: yup.string().when('isPhysicalSameAsMailing', {
         is: false,
         then: (schema) => schema.required("Street Address is required")
       }),
       mailingAddressCity: yup.string().when('isPhysicalSameAsMailing', {
         is: false,
         then: (schema) => schema.required("City is required")
       }),
       mailingAddressState: yup.string().when('isPhysicalSameAsMailing', {
         is: false,
         then: (schema) => schema.required("State/Region is required")
       }),
       mailingAddressZipCode: yup.string().when('isPhysicalSameAsMailing', {
         is: false,
         then: (schema) => schema.required("Postal/Zip Code is required").matches(regex.zipCode, "Enter a valid ZIP/postal code")
       }),
       mailingAddressCountry: yup.string().when('isPhysicalSameAsMailing', {
         is: false,
         then: (schema) => schema.required("Country is required")
       }),
      
      // Registered Agent Business
      registeredAgentName: yup.string().required("Name is required"),
      registeredAgentEmail: yup.string().required("Email is required").matches(regex.email, "Must be a valid email"),
      registeredAgentPhone: yup.string().required("Phone is required").matches(regex.phoneNumber, "Phone number must be 4-12 digits"),
      registeredAgentAddressLine1: yup.string().required("Street Address is required"),
      registeredAgentAddressCity: yup.string().required("City is required"),
      registeredAgentAddressState: yup.string().required("State/Region is required"),
      registeredAgentAddressZipCode: yup.string().required("Postal/Zip Code is required").matches(regex.zipCode, "Enter a valid ZIP/postal code"),
      registeredAgentAddressCountry: yup.string().required("Country is required"),
      
      // Beneficial Owners
      beneficialOwners: yup.array().of(
        yup.object().shape({
          name: yup.string().required("Name is required"),
          percentageOwnership: yup.number().required("Percentage Ownership is required").min(0, "Percentage must be positive").max(100, "Percentage cannot exceed 100"),
          email: yup.string().required("Email is required").matches(regex.email, "Must be a valid email"),
          phone: yup.string().required("Phone is required").matches(regex.phoneNumber, "Phone number must be 4-12 digits")
        })
      ).min(1, "At least one beneficial owner is required")
    })
  ).min(0) // Allow empty array - no businesses required
});

const OnboardingBusiness: React.FC = () => {
  const { user } = useAuth0();
  const api = useApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { register, control, formState: { errors, isDirty }, handleSubmit, setValue, watch } = useForm<BusinessForm>({
    //@ts-ignore
    resolver: yupResolver<BusinessForm>(businessFormSchema),
    defaultValues: {
      businesses: []
    }
  });

  const watchedBusinesses = watch("businesses") || [];
  const visibleBusinesses = watchedBusinesses.filter(business => !business.deleted);

  // Function to convert date from MM/dd/yyyy to yyyy-MM-dd format
  const convertDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns yyyy-MM-dd format
  };

  // Function to handle checkbox change
  const handlePhysicalSameAsMailingChange = (businessIndex: number, checked: boolean): void => {
    // Update the form value immediately so the UI updates
    setValue(`businesses.${businessIndex}.isPhysicalSameAsMailing`, checked);
  };

  useEffect(() => {
    const populateExistingData = async () => {
      try {
        // First try to get existing business data from the database
        const response = await api.get("/api/onboarding/Business");
        if (response.data && response.data.businessEntities && response.data.businessEntities.length > 0) {
          // We have existing business data, populate the form with it
          // Ensure each business has at least one beneficial owner
          const businessesWithBO = response.data.businessEntities.map((business: BusinessDTO) => ({
            ...business,
            dateOfFormation: convertDateForInput(business.dateOfFormation),
            beneficialOwners: business.beneficialOwners && business.beneficialOwners.length > 0
              ? business.beneficialOwners
              : [{} as BeneficialOwner]
          }));

          setValue("businesses", businessesWithBO);
        } else {
          // No existing data, don't create default empty business form
          setValue("businesses", []);
        }
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          if (status === 404) {
            // No business data exists, don't create default empty business form
            setValue("businesses", []);
          } else {
            navigate("/error", {
              state: {
                error: error.response.data || "An error occurred while fetching business data",
                status: status
              }
            });
            return;
          }
        } else {
          navigate("/error", {
            state: {
              error: "Network error. Please check your connection and try again.",
              status: "network"
            }
          });
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    populateExistingData();
  }, [api, user?.email, navigate, setValue]);

  const onSubmit = async (data: BusinessForm) => {
    setSubmitting(true);
    try {
      // Convert form data to include deletedAt field (following InsuranceCompanies pattern)
      const businessDTOs = data.businesses.map(business => ({
        ...business,
        deleted: business.deleted
      }));

      // Submit businesses only if there are any
      const response = await api.post("/api/onboarding/Business", businessDTOs);

      // Check if the response indicates success
      if (response.data && response.data.success === true) {
        // Navigate to trust page after successful submission
        navigate("/onboarding/trust");
      } else {
        // Handle case where API returns success: false
        console.error("API returned success: false", response.data);
        navigate("/error", {
          state: {
            error: response.data.message || "An error occurred while saving business information",
            status: 500
          }
        });
      }
    } catch (error: any) {
      console.error("Error saving business data:", error);
      navigate("/error", {
        state: {
          error: "An error occurred while saving business information",
          status: 500
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const addBeneficialOwner = (businessIndex: number) => {
    const currentBusinesses = watch("businesses") || [];
    const updatedBusinesses = currentBusinesses.map((business, index) =>
      index === businessIndex ? {
        ...business,
        beneficialOwners: [...(business.beneficialOwners || []), { name: "", percentageOwnership: 0, email: "", phone: "" }]
      } : business
    );
    setValue("businesses", updatedBusinesses);
  };

  const removeBeneficialOwner = async (businessIndex: number, ownerIndex: number) => {
    const currentBusinesses = watch("businesses") || [];
    const business = currentBusinesses[businessIndex];
    const beneficialOwner = business?.beneficialOwners?.[ownerIndex];

    if (beneficialOwner?.id && beneficialOwner.id > 0) {
      // Existing beneficial owner - call DELETE API
      try {
        await api.delete(`/api/onboarding/DeleteBeneficialOwner/${beneficialOwner.id}`);
        // Mark as deleted in the UI
        const updatedBusinesses = currentBusinesses.map((b, i) => 
          i === businessIndex ? {
            ...b,
            beneficialOwners: (b.beneficialOwners || []).map((bo, idx) => 
              idx === ownerIndex ? { ...bo, deleted: true } : bo
            )
          } : b
        );
        setValue("businesses", updatedBusinesses);
      } catch (error: any) {
        console.error("Error deleting beneficial owner:", error);
        // Handle error - could show a toast notification
      }
    } else {
      // New beneficial owner - remove from array
      const updatedBusinesses = currentBusinesses.map((b, i) => 
        i === businessIndex ? {
          ...b,
          beneficialOwners: (b.beneficialOwners || []).filter((_, index) => index !== ownerIndex)
        } : b
      );
      setValue("businesses", updatedBusinesses);
    }
  };

  const addBusiness = () => {
    const currentBusinesses = watch("businesses") || [];
    setValue("businesses", [...currentBusinesses, { beneficialOwners: [{} as BeneficialOwner] }] as BusinessDTO[]);
  };

  const removeBusiness = async (displayIndex: number) => {
    const currentBusinesses = watch("businesses") || [];
    const updatedBusinesses = [...currentBusinesses];

    // Find the actual index in the original array by skipping deleted items
    let actualIndex = -1;
    let visibleCount = 0;

    for (let i = 0; i < updatedBusinesses.length; i++) {
      const business = updatedBusinesses[i];
      if (business && !business.deleted) {
        if (visibleCount === displayIndex) {
          actualIndex = i;
          break;
        }
        visibleCount++;
      }
    }

    // Check if we found the business at the given display index
    if (actualIndex >= 0) {
      const business = updatedBusinesses[actualIndex];
      if (business) {
        // Mark the business as deleted instead of removing from array
        if (business.id && business.id > 0) {
          // This is an existing business - call the DELETE API
          try {
            await api.delete(`/api/onboarding/DeleteBusiness/${business.id}`);
            // Mark as deleted in the UI
            updatedBusinesses[actualIndex] = {
              ...business,
              deleted: true
            };
          } catch (error: any) {
            console.error("Error deleting business:", error);
            // Handle error - could show a toast notification
          }
        } else {
          // This is a new business - remove from array
          updatedBusinesses.splice(actualIndex, 1);
        }
      }
    }

    setValue("businesses", updatedBusinesses);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <UnsavedChangesGuard hasUnsavedChanges={isDirty}></UnsavedChangesGuard>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">Business Information</h1>
              <p className="text-lg">Add and manage your businesses. You can skip this step if you don't have any businesses.</p>
            </div>
          </div>

          {/* Business List */}
          {visibleBusinesses.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="text-center py-8">
                <h2 className="text-xl font-semibold text-gray-700 mb-4">No Businesses</h2>
                <p className="text-gray-600 mb-6">
                  If you have businesses to add, click the button below to get started.
                </p>
                <button
                  type="button"
                  onClick={addBusiness}
                  className="btn-primary-lg mx-auto"
                >
                  + Add Your First Business
                </button>
              </div>
            </div>
                      ) : (
              visibleBusinesses.map((business, businessIndex) => {
                // Find the actual index in the original array by counting visible businesses
                let actualIndex = -1;
                let visibleCount = 0;
                
                for (let i = 0; i < watchedBusinesses.length; i++) {
                  const b = watchedBusinesses[i];
                  if (b && !b.deleted) {
                    if (visibleCount === businessIndex) {
                      actualIndex = i;
                      break;
                    }
                    visibleCount++;
                  }
                }
                return (
              <div key={businessIndex} className="bg-white rounded-xl shadow-md p-6">

                {/* Businesses Array Error */}
                {businessIndex === 0 && errors.businesses?.message && <p role="alert" className={css.validationError}>{errors.businesses?.message}</p>}

                {/* Business Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Business {businessIndex + 1}</h2>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={() => removeBusiness(businessIndex)}
                      className="btn-danger"
                    >
                      <TrashIcon />
                      Remove Business
                    </button>
                  </div>
                </div>

                {/* Basic Business Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium border-b pb-2 mb-4">Basic Business Information</h3>

                    <div className="space-y-4">

                      <h4 className="text-md font-medium text-gray-700">Business Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FormInput label="Business Name" name={`businesses.${businessIndex}.name`} required register={register} errors={errors} />

                        <Controller
                          control={control}
                          name={`businesses.${businessIndex}.entityType`}
                          render={({ field }) => (
                            <FormSelect
                              label="Entity Type"
                              required
                              isSearchable={false}
                              isClearable={false}
                              placeholder="Select Entity Type"
                              value={field.value || null}
                              options={ENTITY_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                              onChange={(val) => field.onChange((val as string) ?? '')}
                              error={errors.businesses?.[businessIndex]?.entityType?.message}
                            />
                          )}
                        />

                        <FormInput label="Country of Organization" name={`businesses.${businessIndex}.countryOfOrganization`} required register={register} errors={errors} placeholder="e.g., United States" />

                        <FormInput label="Date of Formation" name={`businesses.${businessIndex}.dateOfFormation`} type="date" required register={register} errors={errors} />
                      </div>

                      <h4 className="text-md font-medium text-gray-700">Contact Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FormInput label="Business Email" name={`businesses.${businessIndex}.businessEmail`} type="email" required register={register} errors={errors} />

                        <FormInput label="Business Phone" name={`businesses.${businessIndex}.businessPhone`} type="tel" required register={register} errors={errors} />

                        <FormInput label="Jurisdiction of Formation" name={`businesses.${businessIndex}.jurisdictionOfFormation`} required register={register} errors={errors} />
                      </div>

                      <h4 className="text-md font-medium text-gray-700">Tax Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FormInput label="Country of Tax Residency" name={`businesses.${businessIndex}.countryOfTaxResidency`} required register={register} errors={errors} placeholder="e.g., United States" />

                        <FormInput label="Business Tax ID" name={`businesses.${businessIndex}.businessTaxId`} required register={register} errors={errors} />
                      </div>
                    </div>
                  </div>

                                     {/* Additional DWP Information */}
                   <div>
                     <h3 className="text-lg font-medium border-b pb-2 mb-4">Additional Business Information</h3>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <div className="space-y-4">
                         <h4 className="text-md font-medium text-gray-700">Address Verification</h4>
                         
                         <div className="flex items-center space-x-2">
                           <input
                             {...register(`businesses.${businessIndex}.canProvideProofOfAddress`)}
                             type="checkbox"
                             id={`can-provide-proof-${businessIndex}`}
                             className="h-4 w-4 custom-checkbox"
                             defaultChecked={true}
                           />
                           <label htmlFor={`can-provide-proof-${businessIndex}`} className={css.label}>
                             Can provide proof of address
                           </label>
                         </div>

                         <div>
                           <label className={css.label}>Proof of Address Explanation</label>
                           <textarea
                             {...register(`businesses.${businessIndex}.proofOfAddressExplanation`)}
                             rows={3}
                             className={css.textBox}
                             placeholder="If you cannot provide proof of address, please explain why..."
                           />
                           {errors.businesses?.[businessIndex]?.proofOfAddressExplanation?.message && <p role="alert" className={css.validationError}>{errors.businesses?.[businessIndex]?.proofOfAddressExplanation?.message}</p>}
                         </div>
                       </div>
                     </div>
                   </div>

                  {/* Primary Contact Information */}
                  <div>
                    <h3 className="text-lg font-medium border-b pb-2 mb-4">Primary Contact Information</h3>

                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-700">Contact Details</h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormInput label="Name" name={`businesses.${businessIndex}.primaryContact`} required register={register} errors={errors} />

                        <FormInput label="Email" name={`businesses.${businessIndex}.primaryContactEmail`} type="email" required register={register} errors={errors} />

                        <FormInput label="Phone" name={`businesses.${businessIndex}.primaryContactPhone`} type="tel" required register={register} errors={errors} />
                      </div>
                    </div>
                  </div>

                  {/* Principal Place of Business */}
                  <div>
                    <h3 className="text-lg font-medium border-b pb-2 mb-4">Principal Place of Business</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <FormInput label="Street Address" name={`businesses.${businessIndex}.legalAddressLine1`} required register={register} errors={errors} />
                      </div>
                      <div className="md:col-span-2">
                        <FormInput label="Address Line 2" name={`businesses.${businessIndex}.legalAddressLine2`} register={register} errors={errors} placeholder="Apartment, suite, etc. (optional)" />
                      </div>
                      <FormInput label="City" name={`businesses.${businessIndex}.legalAddressCity`} required register={register} errors={errors} />
                      <FormInput label="State/Region" name={`businesses.${businessIndex}.legalAddressState`} required register={register} errors={errors} />
                      <FormInput label="Postal/Zip Code" name={`businesses.${businessIndex}.legalAddressZipCode`} required register={register} errors={errors} />
                      <FormInput label="Country" name={`businesses.${businessIndex}.legalAddressCountry`} required register={register} errors={errors} />
                    </div>

                    {/* Physical Same as Mailing Checkbox */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <input
                          {...register(`businesses.${businessIndex}.isPhysicalSameAsMailing`)}
                          type="checkbox"
                          id={`physical-same-as-mailing-${businessIndex}`}
                          className="h-4 w-4 custom-checkbox"
                          onChange={(e) => handlePhysicalSameAsMailingChange(businessIndex, e.target.checked)}
                        />
                        <label htmlFor={`physical-same-as-mailing-${businessIndex}`} className={css.label}>
                          Physical address is the same as mailing address
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Beneficial Owners */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium border-b pb-2">Beneficial Owners</h3>
                      <button
                        type="button"
                        onClick={() => addBeneficialOwner(businessIndex)}
                        className="btn-primary"
                      >
                        + Add Beneficial Owner
                      </button>
                    </div>

                    {/* Beneficial Owners Array Error */}
                    {errors.businesses?.[businessIndex]?.beneficialOwners?.message && <p role="alert" className={css.validationError}>{errors.businesses[businessIndex]?.beneficialOwners?.message}</p>}

                    <div className="space-y-6">
                      {(business.beneficialOwners || []).filter(owner => !owner.deleted).map((owner, ownerIndex) => {
                        // Find the actual index in the original array by counting visible beneficial owners
                        let actualOwnerIndex = -1;
                        let visibleOwnerCount = 0;
                        
                        for (let i = 0; i < (business.beneficialOwners || []).length; i++) {
                          const bo = (business.beneficialOwners || [])[i];
                          if (bo && !bo.deleted) {
                            if (visibleOwnerCount === ownerIndex) {
                              actualOwnerIndex = i;
                              break;
                            }
                            visibleOwnerCount++;
                          }
                        }
                        return (
                        <div key={ownerIndex} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-md font-medium text-gray-700">Beneficial Owner {ownerIndex + 1}</h4>
                            {(business.beneficialOwners || []).filter(bo => !bo.deleted).length > 0 && (
                              <button
                                type="button"
                                onClick={() => removeBeneficialOwner(actualIndex, actualOwnerIndex)}
                                className="btn-danger"
                              >
                                <TrashIcon />
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput label="Name" name={`businesses.${businessIndex}.beneficialOwners.${ownerIndex}.name`} required register={register} errors={errors} />

                            <FormInput label="Percentage Ownership" name={`businesses.${businessIndex}.beneficialOwners.${ownerIndex}.percentageOwnership`} type="number" required register={register} errors={errors} />

                            <FormInput label="Email" name={`businesses.${businessIndex}.beneficialOwners.${ownerIndex}.email`} type="email" required register={register} errors={errors} />

                            <FormInput label="Phone" name={`businesses.${businessIndex}.beneficialOwners.${ownerIndex}.phone`} type="tel" required register={register} errors={errors} />
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Business Mailing Address */}
                  {!business.isPhysicalSameAsMailing && (
                    <div>
                      <h3 className="text-lg font-medium border-b pb-2 mb-4">Business Mailing Address</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <FormInput label="Street Address" name={`businesses.${businessIndex}.mailingAddressLine1`} required register={register} errors={errors} />
                        </div>
                        <div className="md:col-span-2">
                          <FormInput label="Address Line 2" name={`businesses.${businessIndex}.mailingAddressLine2`} register={register} errors={errors} placeholder="Apartment, suite, etc. (optional)" />
                        </div>
                        <FormInput label="City" name={`businesses.${businessIndex}.mailingAddressCity`} required register={register} errors={errors} />
                        <FormInput label="State/Region" name={`businesses.${businessIndex}.mailingAddressState`} required register={register} errors={errors} />
                        <FormInput label="Postal/Zip Code" name={`businesses.${businessIndex}.mailingAddressZipCode`} required register={register} errors={errors} />
                        <FormInput label="Country" name={`businesses.${businessIndex}.mailingAddressCountry`} required register={register} errors={errors} />
                      </div>
                    </div>
                  )}

                  {/* Registered Agent */}
                  <div>
                    <h3 className="text-lg font-medium border-b pb-2 mb-4">Registered Agent</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormInput label="Name" name={`businesses.${businessIndex}.registeredAgentName`} required register={register} errors={errors} />

                      <FormInput label="Email" name={`businesses.${businessIndex}.registeredAgentEmail`} type="email" required register={register} errors={errors} />

                      <FormInput label="Phone" name={`businesses.${businessIndex}.registeredAgentPhone`} type="tel" required register={register} errors={errors} />
                    </div>

                    <div className="mt-6">
                      <h4 className="text-md font-medium text-gray-700 mb-4">Registered Agent Address</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <FormInput label="Street Address" name={`businesses.${businessIndex}.registeredAgentAddressLine1`} required register={register} errors={errors} />
                        </div>
                        <div className="md:col-span-2">
                          <FormInput label="Address Line 2" name={`businesses.${businessIndex}.registeredAgentAddressLine2`} register={register} errors={errors} placeholder="Apartment, suite, etc. (optional)" />
                        </div>
                        <FormInput label="City" name={`businesses.${businessIndex}.registeredAgentAddressCity`} required register={register} errors={errors} />
                        <FormInput label="State/Region" name={`businesses.${businessIndex}.registeredAgentAddressState`} required register={register} errors={errors} />
                        <FormInput label="Postal/Zip Code" name={`businesses.${businessIndex}.registeredAgentAddressZipCode`} required register={register} errors={errors} />
                        <FormInput label="Country" name={`businesses.${businessIndex}.registeredAgentAddressCountry`} required register={register} errors={errors} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                );
              })
            )}
          {visibleBusinesses.length > 0 && (
            <div className="text-center">
              <button
                type="button"
                onClick={addBusiness}
                className="btn-primary-lg mx-auto"
              >
                + Add Another Business
              </button>
            </div>
          )}

          {/* Submit Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold">Continue to Trust Information</h2>
              <p className="text-gray-600 mt-2">Please review the information above. If everything looks correct, click Continue to proceed.</p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => navigate("/onboarding/individual")}
                disabled={submitting}
                className="btn-secondary"
              >
                Go Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? (
                  <>
                    <div className={css.spinner}></div>
                    Continuing...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OnboardingBusiness;
